/**
 * In-memory gate for work that is triggered by navigation but does not need to
 * run on every page change.
 *
 * Timestamps live in module scope only, so a full page load always starts fresh.
 * That is deliberate: a reload is the gesture people use when they want to see
 * current data, and it should not be swallowed by a throttle.
 */

const lastRunAt = Object.create(null);

/**
 * Tells whether `key` may run now, and records the attempt when it may.
 *
 * The timestamp is taken up front rather than on completion, so that several
 * dispatches in the same tick collapse into one instead of racing.
 *
 * @param {string} key identifies the throttled work
 * @param {number} intervalMs minimum distance between two runs
 * @returns {boolean} true when the caller should proceed
 */
export const acquireThrottle = (key, intervalMs) => {
    const previousRunAt = lastRunAt[key];
    const now = Date.now();

    if (typeof previousRunAt === 'number' && now - previousRunAt < intervalMs) {
        return false;
    }

    lastRunAt[key] = now;

    return true;
};

/**
 * Forgets the last run of `key`, so the next caller may proceed immediately.
 * Use it when the throttled work failed and should not hold the slot.
 *
 * @param {string} key identifies the throttled work
 */
export const releaseThrottle = key => {
    delete lastRunAt[key];
};
